import Router from "../router/index";
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

const CLIENT_ID = "066ea2b5-de3f-4aee-ae35-efe55ccb1078";
const AUTH_BASE_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const TOKEN_ENDPOINT = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
const SCOPE = 'openid offline_access api://09d67940-b360-4ea0-9935-c1d11ad2c965/access_as_user';
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

function getRedirectUri() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'msauth.com.prewoe.cap://auth';
  if (platform === 'android') return 'msauth://com.prewoe.cap/sDbOw1JVGt+Tcu8Oql8KcNCyiC0=';
  return process.env.VUE_APP_AUTH_REDIRECT_URI;
}

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

class AzureAuthService {
  constructor() {
    this._urlListener = null;
    this._refreshPromise = null;
  }

  _registerUrlListener() {
    if (this._urlListener) return;

    this._urlListener = App.addListener('appUrlOpen', async ({ url }) => {
      const platform = Capacitor.getPlatform();
      const isCallback =
        (platform === 'android' && url.startsWith('msauth://com.prewoe.cap')) ||
        (platform === 'ios' && url.startsWith('msauth.com.prewoe.cap://auth'));

      if (isCallback) {
        await this._removeUrlListener();
        await this.handleCallback(url);
      }
    });
  }

  async _removeUrlListener() {
    if (this._urlListener) {
      const listener = await this._urlListener;
      listener.remove();
      this._urlListener = null;
    }
  }

  async login(redirectUrl) {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    sessionStorage.setItem('pkce_verifier', codeVerifier);
    if (redirectUrl) sessionStorage.setItem('login_redirect', redirectUrl);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: getRedirectUri(),
      scope: SCOPE,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      prompt: 'select_account',
    });

    const authUrl = `${AUTH_BASE_URL}?${params}`;

    if (Capacitor.isNativePlatform()) {
      this._registerUrlListener();
      await Browser.open({ url: authUrl });
    } else {
      window.location.href = authUrl;
    }
  }

  async handleCallback(url) {
    const queryStart = url.indexOf('?');
    const params = new URLSearchParams(queryStart !== -1 ? url.substring(queryStart) : '');
    const code = params.get('code');

    if (!code) {
      console.error('No authorization code in callback URL');
      Router.push({ name: 'login' });
      return;
    }

    const codeVerifier = sessionStorage.getItem('pkce_verifier');
    const redirectUrl = sessionStorage.getItem('login_redirect');
    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('login_redirect');

    try {
      const body = new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'authorization_code',
        code,
        redirect_uri: getRedirectUri(),
        code_verifier: codeVerifier,
      }).toString();

      let data;
      if (Capacitor.isNativePlatform()) {
        const result = await CapacitorHttp.request({
          method: 'POST',
          url: TOKEN_ENDPOINT,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          data: body,
        });
        data = result.data;
      } else {
        const response = await fetch(TOKEN_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        });
        data = await response.json();
      }

      if (!data.access_token) {
        console.error('Token exchange error response:', JSON.stringify(data));
        throw new Error(data.error_description || data.error || 'No access token in response');
      }

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('tokenExpiry', Date.now() + (data.expires_in * 1000));
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('azureUser', true);

      if (Capacitor.isNativePlatform()) {
        await Browser.close().catch(err => console.warn('Browser.close failed:', err));
      }

      Router.push({ name: 'loginsuccess', query: { nextUrl: redirectUrl } });
    } catch (error) {
      console.error('Token exchange failed:', error);
      await this._removeUrlListener();
      if (Capacitor.isNativePlatform()) {
        await Browser.close().catch(() => {});
      }
      Router.push({ name: 'login' });
    }
  }

  // Called by LoginAdSuccessView after web redirect
  loggedIn() {
    this.handleCallback(window.location.href);
  }

  async logout() {
    await this._removeUrlListener();
    localStorage.clear();
  }

  async refreshToken() {
    const azureUser = localStorage.getItem('azureUser');
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenExpiry = parseInt(localStorage.getItem('tokenExpiry'), 10);

    if (!azureUser || !refreshToken) return;
    if (tokenExpiry && Date.now() < tokenExpiry - TOKEN_REFRESH_BUFFER_MS) return;

    // Prevent concurrent refreshes — reuse in-flight promise if one exists
    if (this._refreshPromise) return this._refreshPromise;
    this._refreshPromise = this._doRefresh().finally(() => {
      this._refreshPromise = null;
    });
    return this._refreshPromise;
  }

  async _doRefresh() {
    const refreshToken = localStorage.getItem('refreshToken');

    try {
      const body = new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        redirect_uri: getRedirectUri(),
        scope: SCOPE,
      }).toString();

      let data;
      if (Capacitor.isNativePlatform()) {
        const result = await CapacitorHttp.request({
          method: 'POST',
          url: TOKEN_ENDPOINT,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          data: body,
        });
        data = result.data;
      } else {
        const response = await fetch(TOKEN_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        });
        data = await response.json();
      }

      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        localStorage.setItem('tokenExpiry', Date.now() + (data.expires_in * 1000));
      } else {
        console.error('OAuth2 Refresh Failed:', data);
        localStorage.clear();
        await this.login(Router.currentRoute.fullPath);
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.clear();
      await this.login(Router.currentRoute.fullPath);
    }
  }
}

export default new AzureAuthService();
